import React, { useEffect, useState } from 'react';
import {
  ClipboardList, Stethoscope, HeartPulse, Bell, Repeat,
  Star, CalendarCheck, MessageCircle, Wrench, Users,
  Plus, Pencil, Check, X,
} from 'lucide-react';
import {
  useActivityTypesStore,
  CUSTOM_TYPE_ICONS,
  type CustomActivityType,
} from '../../lib/stores/activityTypesStore';
import { useApp } from '../AppProvider';
import { Switch } from '../ui/switch';

const ICON_COMPONENTS: Record<string, React.ComponentType<{ className?: string; strokeWidth?: number }>> = {
  'clipboard-list': ClipboardList,
  'stethoscope': Stethoscope,
  'heart-pulse': HeartPulse,
  'bell': Bell,
  'repeat': Repeat,
  'star': Star,
  'calendar-check': CalendarCheck,
  'message-circle': MessageCircle,
  'wrench': Wrench,
  'users': Users,
};

/**
 * `strokeWidth` è opzionale e additivo: i consumer che non lo passano continuano a usare lo
 * spessore di default di lucide, quindi nessuno cambia resa. Serve al composer inline, dove
 * il redesign impone stroke 1.5 su tutte le icone.
 */
export function ActivityTypeIcon({ icon, className, strokeWidth }: { icon: string; className?: string; strokeWidth?: number }) {
  const Cmp = ICON_COMPONENTS[icon] || ClipboardList;
  return <Cmp className={className || 'h-4 w-4'} strokeWidth={strokeWidth} />;
}

function IconPicker({ value, onChange }: { value: string; onChange: (icon: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {CUSTOM_TYPE_ICONS.map((icon) => (
        <button
          key={icon}
          type="button"
          onClick={() => onChange(icon)}
          className={`flex items-center justify-center h-9 w-9 rounded-lg border-2 transition-all ${
            value === icon
              ? 'border-black bg-black/5 text-black'
              : 'border-black/20 hover:border-black/40 text-black/60'
          }`}
        >
          <ActivityTypeIcon icon={icon} className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
}

// Converte un errore Supabase/generico in un messaggio leggibile per l'utente.
function getErrorMessage(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null && 'code' in err && (err as { code?: string }).code === '23505') {
    return 'Esiste già un tipo attività con questo nome';
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function CustomTypeRow({ type }: { type: CustomActivityType }) {
  const { updateCustomType, setActive } = useActivityTypesStore();
  const { toast } = useApp();
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(type.name);
  const [editIcon, setEditIcon] = useState(type.icon);
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setEditName(type.name);
    setEditIcon(type.icon);
    setEditing(true);
  };

  const handleSave = async () => {
    const trimmed = editName.trim();
    if (!trimmed) {
      toast.error('Il nome non può essere vuoto');
      return;
    }
    setSaving(true);
    try {
      await updateCustomType(type.id, { name: trimmed, icon: editIcon });
      toast.success('Tipo attività aggiornato');
      setEditing(false);
    } catch (err) {
      toast.error(getErrorMessage(err, "Errore nell'aggiornamento del tipo attività"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (checked: boolean) => {
    try {
      await setActive(type.id, checked);
      if (!checked) {
        toast.info('Il tipo resta sulle attività esistenti ma non sarà più selezionabile');
      } else {
        toast.success('Tipo attività riattivato');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Errore nell'aggiornamento del tipo attività"));
    }
  };

  if (editing) {
    return (
      <div className="rounded-lg border border-black/20 p-3 space-y-3">
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-black focus:outline-none"
          placeholder="Nome tipo attività"
        />
        <IconPicker value={editIcon} onChange={setEditIcon} />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-black/60 hover:bg-black/5"
          >
            <X className="h-4 w-4" />
            Annulla
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-black text-white hover:bg-black/80 disabled:opacity-50"
          >
            <Check className="h-4 w-4" />
            Salva
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between rounded-lg border border-black/20 p-3">
      <div className="flex items-center gap-3">
        <span className="text-black/60">
          <ActivityTypeIcon icon={type.icon} className="h-4 w-4" />
        </span>
        <span className={`font-medium ${type.active ? 'text-black' : 'text-black/40'}`}>
          {type.name}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={startEdit}
          className="p-1.5 rounded-lg text-black/60 hover:bg-black/5 hover:text-black"
          title="Rinomina"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <Switch checked={type.active} onCheckedChange={handleToggleActive} />
      </div>
    </div>
  );
}

export function CustomActivityTypes() {
  const { customTypes, loading, fetched, fetchCustomTypes, createCustomType } = useActivityTypesStore();
  const { toast } = useApp();
  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState<string>(CUSTOM_TYPE_ICONS[0]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchCustomTypes();
  }, [fetchCustomTypes]);

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      toast.error('Inserisci un nome per il tipo attività');
      return;
    }
    setCreating(true);
    try {
      const created = await createCustomType({ name: trimmed, icon: newIcon });
      if (!created) {
        toast.error('Errore nella creazione del tipo attività');
        return;
      }
      toast.success('Tipo attività creato con successo');
      setNewName('');
      setNewIcon(CUSTOM_TYPE_ICONS[0]);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Errore nella creazione del tipo attività'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="rounded-lg border border-black/20 p-6">
      <h4 className="text-md font-medium text-black mb-1">
        Tipi attività personalizzati
      </h4>
      <p className="text-sm text-black/60 mb-4">
        Crea tipi di attività aggiuntivi oltre a quelli di sistema. I tipi disattivati restano
        visibili sulle attività esistenti ma non sono più selezionabili per le nuove.
      </p>

      {loading && !fetched ? (
        <div className="flex items-center justify-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
        </div>
      ) : (
        <div className="space-y-2 mb-6">
          {customTypes.length === 0 ? (
            <p className="text-sm text-black/40 italic">Nessun tipo attività personalizzato</p>
          ) : (
            customTypes.map((type) => <CustomTypeRow key={type.id} type={type} />)
          )}
        </div>
      )}

      <div className="rounded-lg border border-black/10 bg-black/[0.02] p-4 space-y-3">
        <p className="text-sm font-medium text-black">Aggiungi nuovo tipo</p>
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Nome tipo attività (es. Visita di controllo)"
          className="w-full px-3 py-2 rounded-lg border border-black/20 focus:border-black focus:outline-none"
        />
        <IconPicker value={newIcon} onChange={setNewIcon} />
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              creating
                ? 'bg-black/10 text-black/40 cursor-not-allowed'
                : 'bg-black text-white hover:bg-black/80'
            }`}
          >
            <Plus className="h-4 w-4" />
            Aggiungi tipo
          </button>
        </div>
      </div>
    </div>
  );
}
